/* -----------------------------
    SLIDER COMPONENT
   ----------------------------- */
class BaseSlider extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      slidesPerView: "auto",
      spaceBetween: 0,
      navigation: false,
      pagination: false,
      scrollbar: false,
      grabCursor: true,
      on: {
        afterInit: () => {
          this.classList.add(this.modifiers.initialized);
        }
      }
    };

    this.swiperInstance = null;
  }

  connectedCallback() {
    this.addEventListener("slider:init", this.initSlider.bind(this));
    this.addEventListener("slider:update", this.updateSlider.bind(this));
    this.addEventListener("slider:destroy", this.destroySlider.bind(this));

    this.initSlider();
    this.stopPropagationForScrollableChildren();
  }

  disconnectedCallback() {
    this.removeEventListener("slider:init", this.initSlider.bind(this));
    this.removeEventListener("slider:update", this.updateSlider.bind(this));
    this.removeEventListener("slider:destroy", this.destroySlider.bind(this));

    this.destroySlider();
  }

  get selectors() {
    return {
      wrapper: ".swiper-wrapper",
      slide: "slider-slide"
    }
  }

  get modifiers() {
    return {
      initialized: "initialized"
    }
  }

  get attributes() {
    return {
      pagination: "pagination",
      scrollbar: "scrollbar",
      prevButton: "prev-button",
      nextButton: "next-button",
      freeMode: "freemode",
      fade: "fade",
      centerInsufficientSlides: "center-insufficient-slides",
      initialSlide: "initial-slide",
      vertical: "vertical"
    }
  }

  get options() {
    const
      paginationSelector = this.getAttribute(this.attributes.pagination),
      paginationElement = paginationSelector ? this.querySelector(paginationSelector) : null;

    const
      scrollbarSelector = this.getAttribute(this.attributes.scrollbar),
      scrollbarElement = scrollbarSelector ? this.querySelector(scrollbarSelector) : null;

    const
      prevButtonSelector = this.getAttribute(this.attributes.prevButton),
      prevButtonElement = prevButtonSelector ? this.querySelector(prevButtonSelector) ?? document.querySelector(prevButtonSelector)  : null;

    const
      nextButtonSelector = this.getAttribute(this.attributes.nextButton),
      nextButtonElement = nextButtonSelector ? this.querySelector(nextButtonSelector) ?? document.querySelector(nextButtonSelector) : null;

    const freeMode = this.getAttribute(this.attributes.freeMode) === "true";

    const fade = this.getAttribute(this.attributes.fade) === "true";

    const centerInsufficientSlides = this.getAttribute(this.attributes.centerInsufficientSlides) === "true";

    const initialSlide = this.getAttribute(this.attributes.initialSlide) ?? 0;

    const vertical = this.getAttribute(this.attributes.vertical) === "true";

    const customOptions = {
      ...(paginationElement && {
        pagination: {
          el: paginationElement,
          clickable: true
        }
      }),
      ...(scrollbarElement && {
        scrollbar: {
          el: scrollbarElement,
          draggable: false
        }
      }),
      ...(prevButtonElement && nextButtonElement && {
        navigation: {
          prevEl: prevButtonElement,
          nextEl: nextButtonElement
        }
      }),
      ...(freeMode && {
        freeMode: true
      }),
      ...(fade && {
        effect: "fade"
      }),
      ...(centerInsufficientSlides && {
        centerInsufficientSlides: true
      }),
      ...(initialSlide && {
        initialSlide
      }),
      ...(vertical && {
        direction: "vertical",
        slidesPerView: 1,
        speed: 800
      })
    };

    return {...this.defaultOptions, ...customOptions};
  }

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  set options(options) {
    this._options = {...this._options, ...options};
  }

  initSlider() {
    if (this.swiperInstance) {
      return;
    }

    this.swiperInstance = new window.Swiper(this, this.options);
  }

  destroySlider(deleteInstance = true, clearStyles = true) {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  updateSlider() {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.update();
  }

  stopPropagationForScrollableChildren() {
    const scrollableChildren = [...this.querySelectorAll(".swiper-no-swiping")];

    if (!scrollableChildren.length) {
      return;
    }

    scrollableChildren.forEach((scrollableChild) => {
      scrollableChild.addEventListener("touchmove", function (event) {
        event.stopPropagation();
      });
    });
  }
}

if (!customElements.get("base-slider")) {
  customElements.define("base-slider", BaseSlider);
}

class ScrollSlider extends BaseSlider {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(this.initSlider.bind(this));
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
  }

  get modifiers() {
    return {
      ...super.modifiers,
      bleed: "slider--bleed",
      scroll: "slider--scroll"
    }
  }

  get breakpoints() {
    return {
      desktop: 992,
      mobile: 375
    }
  }

  get wrapper() {
    return this.querySelector(this.selectors.wrapper);
  }

  initSlider() {
    if (window.innerWidth < this.breakpoints.desktop) {
      this.classList.remove(this.modifiers.bleed);
      this.classList.add(this.modifiers.scroll);

      if (this.swiperInstance) {
        this.destroySlider();
      }
    } else {
      this.classList.add(this.modifiers.bleed);
      this.classList.remove(this.modifiers.scroll);

      if (!this.swiperInstance) {
        this.swiperInstance = new window.Swiper(this, this.options);
      }
    }
  }
}

if (!customElements.get("scroll-slider")) {
  customElements.define("scroll-slider", ScrollSlider);
}

class ProductOptionSlider extends ScrollSlider {
  constructor() {
    super();
  }

  get selectors() {
    return {
      ...super.selectors,
      slide: "[data-slide]",
      initialSlide: "[data-initial-slide]"
    }
  }

  get initialSlide() {
    return this.querySelector(this.selectors.initialSlide);
  }

  get options() {
    const initialSlideIndex = this.initialSlide ? this.slides.indexOf(this.initialSlide) : null;

    return {
      ...super.options,
      slidesPerGroup: 3,
      ...(initialSlideIndex && initialSlideIndex > -1 && {
        initialSlide: initialSlideIndex
      })
    }
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  initSlider() {
    super.initSlider();

    if (this.initialSlide) {
      this.wrapper.scrollLeft = this.initialSlide.offsetLeft - parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--container-padding")
      );
    }
  }
}

if (!customElements.get("product-option-slider")) {
  customElements.define("product-option-slider", ProductOptionSlider);
}

class FilterableSlider extends BaseSlider {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("slider:filter", this.filterSlider.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("slider:filter", this.filterSlider.bind(this));
  }

  get modifiers() {
    return {
      ...super.modifiers,
      hidden: "hidden"
    }
  }

  get attributes() {
    return {
      ...super.attributes,
      filters: "filters"
    }
  }

  get resetFilterValue() {
    return "reset";
  }

  filterSlider(event) {
    const { filterValue } = event?.detail;

    if (!filterValue) {
      return;
    }

    const [slidesToShow, slidesToHide] = this.filterSlidesByValue(filterValue);

    slidesToHide.forEach((item) => {
      item.classList.add(this.modifiers.hidden);
    });

    slidesToShow.forEach((item) => {
      item.classList.remove(this.modifiers.hidden);
    });

    this.updateSlider();
  }

  filterSlidesByValue(value = this.resetFilterValue) {
    const items = this.slides;

    if (value === this.resetFilterValue) {
      return [[...items], []];
    }

    let [itemsToShow, itemsToHide] = [...items].reduce(
      ([itemsToShow, itemsToHide], item) => {
        const filters = item.getAttribute(this.attributes.filters);

        if (!filters) {
          return [[...itemsToShow], [...itemsToHide, item]];
        }

        return filters?.includes(value)
          ? [[...itemsToShow, item], [...itemsToHide]]
          : [[...itemsToShow], [...itemsToHide, item]];
      },
      [[], []]
    );

    if (!itemsToShow.length) {
      itemsToShow = [...items];
    }

    return [itemsToShow, itemsToHide];
  }
}

if (!customElements.get("filterable-slider")) {
  customElements.define("filterable-slider", FilterableSlider);
}

class EmbeddedControlsSlider extends BaseSlider {
  constructor() {
    super();
  }

  get selectors() {
    return {
      ...super.selectors,
      button: {
        prev: "slider-button[type='prev']",
        next: "slider-button[type='next']"
      }
    }
  }

  get modifiers() {
    return {
      ...super.modifiers,
      button: {
        disabled: "swiper-button-disabled"
      }
    }
  }

  buttonTypes = {
    prev: "prev",
    next: "next"
  };

  initSlider() {
    super.initSlider();

    this.buttonsPrev = [...this.querySelectorAll(this.selectors.button.prev)];
    this.buttonsNext = [...this.querySelectorAll(this.selectors.button.next)];

    this.buttonsPrev.forEach((button) => {
      button.addEventListener("click", () => {
        this.onButtonClick(this.buttonTypes.prev);
      });
    });

    this.buttonsNext.forEach((button) => {
      button.addEventListener("click", () => {
        this.onButtonClick(this.buttonTypes.next);
      });
    });

    this.updateButtons();

    this.swiperInstance.on("slideChange", () => this.updateButtons());
  }

  onButtonClick(type) {
    if (!this.swiperInstance) {
      return;
    }

    switch (type) {
      case this.buttonTypes.prev: {
        this.swiperInstance.slidePrev();
        break;
      }

      case this.buttonTypes.next: {
        this.swiperInstance.slideNext();
        break;
      }
    }

    this.updateButtons();
  }

  updateButtons() {
    if (!this.swiperInstance) {
      return;
    }

    const isAtFirstSlide = this.swiperInstance.activeIndex === 0;
    const isAtLastSlide = this.swiperInstance.activeIndex === (this.slides.length - 1);

    this.buttonsPrev.forEach((button) => {
      button.setAttribute("aria-disabled", isAtFirstSlide.toString());
      button.classList.toggle(this.modifiers.button.disabled, isAtFirstSlide);
    });

    this.buttonsNext.forEach((button) => {
      button.setAttribute("aria-disabled", isAtLastSlide.toString());
      button.classList.toggle(this.modifiers.button.disabled, isAtLastSlide);
    });
  }
}

if (!customElements.get("embedded-controls-slider")) {
  customElements.define("embedded-controls-slider", EmbeddedControlsSlider);
}

class PromotionDealsSlider extends EmbeddedControlsSlider {
  constructor() {
    super();
  }

  initSlider() {
    super.initSlider();

    document.addEventListener("countdown:init", () => {
      const timer = this.querySelector("countdown-timer[state='current']");

      if (!timer) {
        return;
      }

      const slide = timer.closest(this.selectors.slide);

      if (!slide) {
        return;
      }

      this.swiperInstance.slideTo(slide.getAttribute("index"));
      this.updateButtons();
    });
  }
}

if (!customElements.get("promotional-deals-slider")) {
  customElements.define("promotional-deals-slider", PromotionDealsSlider);
}
